import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  useRetailAccounts as useRetailAccountsQuery,
  useAccountContent,
  usePatchAccountMutation,
  useRebalanceMutation,
} from '../../queries/retailAccounts';
import { useOnboardingStatus } from '../../queries/onboarding';
import { retailAccountKeys } from '../../queries/queryKeys';
import {
  pickDefaultAccountId,
  setStoredPortfolioAccountId,
} from './portfolioAccountStorage';

const extractError = (err: unknown) =>
  (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? undefined;

export const usePortfolioOverview = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  const {
    data: allAccounts = [],
    isLoading: accountsLoading,
    error: accountsError,
  } = useRetailAccountsQuery();

  const { data: onboardingStatus } = useOnboardingStatus();

  // Pick the default account once accounts are loaded
  useEffect(() => {
    if (accountsLoading) return;
    if (allAccounts.length === 0) return;

    setSelectedAccountId((prev) => {
      if (prev && allAccounts.some((a) => a.id === prev)) return prev;
      return pickDefaultAccountId(allAccounts);
    });
  }, [accountsLoading, allAccounts]);

  // Navigate away when there are no accounts
  useEffect(() => {
    if (accountsLoading || allAccounts.length > 0) return;
    const dest = onboardingStatus?.isComplete ? '/profile/portfolios' : '/onboarding';
    navigate(dest, { replace: true });
  }, [accountsLoading, allAccounts, onboardingStatus, navigate]);

  // Navigate away on a hard accounts-fetch error
  useEffect(() => {
    if (!accountsError) return;
    toast.error(extractError(accountsError) ?? 'Failed to load accounts');
    navigate('/onboarding', { replace: true });
  }, [accountsError, navigate]);

  const account = useMemo(
    () => allAccounts.find((a) => a.id === selectedAccountId) ?? null,
    [allAccounts, selectedAccountId],
  );

  const {
    data: content,
    isFetching: contentLoading,
    error: contentErr,
    dataUpdatedAt,
  } = useAccountContent(selectedAccountId, { enabled: !!selectedAccountId });

  const contentError = contentErr ? (extractError(contentErr) ?? 'Failed to load portfolio content') : null;
  const contentUpdatedAt = useMemo(
    () => (dataUpdatedAt ? new Date(dataUpdatedAt) : null),
    [dataUpdatedAt],
  );

  const patchAccount = usePatchAccountMutation();
  const rebalance = useRebalanceMutation();

  const selectAccount = useCallback(
    (id: string) => {
      const match = allAccounts.find((a) => a.id === id);
      if (!match) return;
      setSelectedAccountId(id);
      setStoredPortfolioAccountId(id);
    },
    [allAccounts],
  );

  // Invalidates content AND all history ranges for this account
  const handleRefresh = useCallback(async () => {
    if (!selectedAccountId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: retailAccountKeys.content(selectedAccountId) }),
      queryClient.invalidateQueries({ queryKey: retailAccountKeys.historyAll(selectedAccountId) }),
    ]);
  }, [queryClient, selectedAccountId]);

  const handleRebalance = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      await rebalance.mutateAsync(selectedAccountId);
      toast.success('Rebalance triggered successfully');
    } catch (error: unknown) {
      toast.error(extractError(error) ?? 'Failed to trigger rebalance');
    }
  }, [selectedAccountId, rebalance]);

  const handleToggleActive = useCallback(async () => {
    if (!account) return;
    try {
      await patchAccount.mutateAsync({ id: account.id, patch: { active: !account.active } });
      toast.success(`Account ${account.active ? 'deactivated' : 'activated'}`);
    } catch (error: unknown) {
      toast.error(extractError(error) ?? 'Failed to update account');
    }
  }, [account, patchAccount]);

  return {
    account,
    allAccounts,
    content,
    accountsLoading,
    contentLoading,
    contentError,
    updating: patchAccount.isPending,
    rebalancing: rebalance.isPending,
    selectAccount,
    handleRefresh,
    handleRebalance,
    handleToggleActive,
    contentUpdatedAt,
  };
};
